import BoonButterflyIcon from '@assets/svg/boon-butterfly.svg';
import FacebookLoginButton from '@common/auth/facebook-login';
import LoginForm from '@common/auth/login-form';
import Modal from '@common/modal/modal';
import { useQuery } from '@hooks/use-query';
import { login as doLogin } from '@redux/auth/auth-action';
import AuthState from '@redux/auth/auth-state.interface';
import { AuthActions } from '@redux/auth/auth-types';
import { hideModal, showModal } from '@redux/modal/modal-dispatcher';
import ModalState, { ModalActionSource } from '@redux/modal/modal-state.interface';
import * as Sentry from '@sentry/browser';
import { authService } from '@dev.boon/brent';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import styles from './modal.styles';

const LoginModal: React.FC = () => {
    const { t } = useTranslation();
    const query = useQuery();

    const dispatch = useDispatch<ThunkDispatch<AuthState, undefined, AuthActions>>();
    const show = useSelector((state: { modal: ModalState }) => state.modal.show);
    const option = useSelector((state: { modal: ModalState }) => state.modal.option);

    const [showVerifMsg, setVerifyMsgShow] = useState(false);
    const [showVerifError, setVerifyErrorShow] = useState(false);
    const [changePasswordMsg, setChangePasswordMsg] = useState(false);
    const [showGenericError, setGenericErrorShow] = useState(false);
    const [showLoginError, setLoginErrorShow] = useState(false);
    const [showDeactivatedAccountError, setDeactivatedAcccountErrorShow] = useState(false);
    const [verifNotification, showVerifNotification] = useState(false);

    const redirectToReQuery = url => {
        const accessToken = authService.getAccessToken() || '';
        url.searchParams.append('accessToken', accessToken);
        // we use window location here since the redirection could be outside wolfe app
        window.location.href = url.toString();
    };

    const onClickSubmit = () => {
        setGenericErrorShow(false);
        setLoginErrorShow(false);
    };

    const onLoginSubmit = async (email, password) => {
        try {
            setGenericErrorShow(false);
            setLoginErrorShow(false);

            await dispatch(doLogin(email, password));

            const getUrl = new URL(window.location.href);
            let re = getUrl.searchParams.get('re');
            // check if there's a redirectUrl from modal options
            if (option && option.redirectUrl) {
                re = option.redirectUrl;
            }

            if (re) {
                const url = new URL(decodeURI(re));
                if (url.hostname.includes('booninterior.com')) {
                    redirectToReQuery(url);
                }
            }

            hideModal('login');
        } catch (error) {
            if (error.response && typeof error.response.status !== 'undefined' && error.response.status === 401) {
                setLoginErrorShow(true);
            } else if (
                error.response &&
                typeof error.response.status !== 'undefined' &&
                error.response.status === 403
            ) {
                setDeactivatedAcccountErrorShow(true);
            } else {
                Sentry.captureException(error);
                setGenericErrorShow(true);
            }
            showModal('login');
        }
    };

    const onClickSignup = (event: Event) => {
        event.preventDefault();

        let re = query.re;
        // check if there's a redirectUrl from modal options
        if (option && option.redirectUrl) {
            re = option.redirectUrl;
        }

        hideModal('login');

        if (option && option.modalActionSource) {
            showModal('signup', { redirectUrl: re, modalActionSource: option.modalActionSource });
        } else {
            showModal('signup', { redirectUrl: re });
        }
    };

    const onClickForgot = event => {
        event.preventDefault();

        hideModal('login');
        showModal('forgot-password');
    };

    useEffect(() => {
        if (query.re) {
            showModal('login');

            const url = new URL(decodeURI(query.re));
            const user = authService.getCurrentUser();
            if (url.hostname.includes('booninterior.com') && user && Object.keys(user).length !== 0) {
                redirectToReQuery(url);
            }
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                // set initial messages to false
                setVerifyMsgShow(false);
                setGenericErrorShow(false);
                setLoginErrorShow(false);
                setVerifyErrorShow(false);
                setChangePasswordMsg(false);
                if (!verifNotification) {
                    let verifCode = query.verifCode;
                    const changePassword = query.changePassword;

                    if (Array.isArray(verifCode)) {
                        verifCode = verifCode[0];
                    }

                    if (verifCode) {
                        const result = await authService.verify(verifCode);

                        // check if we affect if the verif code is legit
                        if (result.verified === 1 && result.role.includes('user')) {
                            setVerifyMsgShow(true);
                            showModal('login');
                        } else if (result.verified === 1 && result.role.includes('partner')) {
                            showModal('verify-partner-success');
                        } else {
                            setVerifyErrorShow(true);
                            showModal('login');
                        }
                        showVerifNotification(true);
                    }
                    // add set timeout to wait for ease in
                    setTimeout(() => {
                        if (changePassword) {
                            showModal('login');
                            setChangePasswordMsg(true);
                            showVerifNotification(true);
                        }
                    }, 50);
                }
            } catch (error) {
                showModal('login');
                setVerifyErrorShow(true);
                Sentry.captureException(error);
            }
        })();
    }, [show]);

    return (
        <Modal id="login" contentWrapperClass="relative text-left" titleBarClass="mt-4 mr-4">
            <div className="-mt-4 px-4">
                <style jsx>{styles}</style>

                <img width="35" className="mr-auto ml-auto" src={BoonButterflyIcon} />

                <LoginForm
                    onSubmit={onLoginSubmit}
                    onClickSubmit={onClickSubmit}
                    onClick={onClickSignup}
                    actionSource={option ? option.modalActionSource : ModalActionSource.Default}
                />

                {(showLoginError || showDeactivatedAccountError || showGenericError) && (
                    <div className={'ErrorLogin mb-2 text-center text-error-red text-sm leading-6'}>
                        {showLoginError && <React.Fragment>{t('validation:invalid-login')}</React.Fragment>}
                        {showDeactivatedAccountError && (
                            <React.Fragment>{t('validation:deactivated-account')}</React.Fragment>
                        )}
                        {showGenericError && <React.Fragment>{t('validation:error-generic')}</React.Fragment>}
                    </div>
                )}

                {(showVerifMsg || showVerifError || changePasswordMsg) && (
                    <div className={'HeaderMessage block px-5 pt-2 mb-2 text-center text-sm leading-4'}>
                        {showVerifMsg && (
                            <span className="VerifiedSuccess text-slate">{t('base:verified-user-message')}</span>
                        )}
                        {showVerifError && (
                            <span className="VerifiedError text-error-red">{t('base:verified-user-error')}</span>
                        )}
                        {changePasswordMsg && (
                            <span className="changePasswordSuccess text-slate">
                                {t('base:change-password-user-message')}
                            </span>
                        )}
                    </div>
                )}

                <div className="px-5 pt-2 pb-3 text-clay tracking-wider">
                    {t('base:cant-login')}{' '}
                    <a href="#" className="CantLogin underline hover:no-underline text-clay" onClick={onClickForgot}>
                        {t('base:cant-login-here')}
                    </a>
                </div>
            </div>

            <div className="bg-super-boon px-8 py-4">
                <div className="flex flex-row-reverse flex-no-wrap">
                    <div className="flex-grow-0 self-center">
                        <div className="pl-2">
                            <FacebookLoginButton />
                        </div>
                    </div>
                    <div className="flex-grow self-center text-white font-gr-light tracking-wider font-hairline">
                        {t('social-media-login-text')}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default LoginModal;
